/**
 * Custom no-cap collection filter — Stage B: owns all filter/sort/page/
 * view state and renders the product grid, once collection-custom-facets.js
 * has the full dataset loaded (window.SF_FULL_PRODUCTS_LOADED).
 *
 * Grid rendering is entirely client-side, synchronous, from the same full
 * dataset already fetched for the sidebar (collection-custom-facets.js's
 * Storefront API query now also pulls image/title/price/availability) --
 * no network request happens on a filter/sort/page/view change at all.
 *
 * This replaces an earlier version that fetched sections/main-collection-
 * product-custom.liquid via Shopify's Section Rendering API per change:
 * confirmed via direct testing (pasting a freshly-parameterized section-
 * render URL straight into a new browser tab, bypassing this script
 * entirely) that Shopify's own server/CDN was returning a cached response
 * for that endpoint regardless of the query string requested -- not
 * something fixable from theme code. Rendering cards directly from data
 * already in memory sidesteps that layer completely, and is faster too.
 *
 * The sidebar itself is re-rendered instantly and entirely client-side
 * from the same dataset (collection-custom-facets.js's SFC_renderSidebar).
 *
 * State (active filters, price range, sort, page, view) round-trips
 * through the URL as cff/cfmin/cfmax/cfsort/cfpage/view so results are
 * shareable/bookmarkable and survive a refresh, without colliding with
 * Shopify's own filter, sort_by, or page params (never used by this system).
 */
(function () {
  'use strict';

  if (window.SFC_controllerLoaded) return;
  window.SFC_controllerLoaded = true;

  var PAGE_SIZE_CAP = 15;
  var state = {
    activeFilters: {},
    priceMin: null,
    priceMax: null,
    sort: '',
    page: 1,
    view: window.location.search.indexOf('view=list') !== -1 ? 'list' : 'grid',
    columns: (function () {
      var el = document.getElementById('product-grid');
      return (el && el.getAttribute('data-view-mode')) || '4';
    })()
  };
  var hasHydrated = false;

  // ---------- small utils ----------

  function getPageSize() {
    var el = document.getElementById('productgridcontainer');
    var size = el ? parseInt(el.getAttribute('data-page-size'), 10) : 0;
    if (!(size > 0)) size = 15;
    return Math.min(size, PAGE_SIZE_CAP);
  }

  function getCollectionUrl() {
    var el = document.getElementById('productgridcontainer');
    return (el && el.getAttribute('data-collection-url')) || window.location.pathname;
  }

  function escapeHtml(str) {
    return String(str).replace(/[&<>"']/g, function (c) {
      return { '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c];
    });
  }

  // Keep in sync with the identical helper in collection-custom-facets.js
  // -- a metafield value can hold multiple comma-separated values on one
  // product; a product matches a filter checked on ANY one of them, not
  // just an exact whole-string match.
  function splitFieldValues(value, key) {
    if (value === undefined || value === null || value === '') return [];
    if (key === 'wattage') return [String(value)];
    if (key === 'cct_kelvin') return [String(value)];
    return String(value)
      .split(',')
      .map(function (v) { return v.trim(); })
      .filter(function (v) { return v !== ''; });
  }

  function money(amount) {
    if (isNaN(amount)) return '';
    return '$' + Number(amount).toFixed(2);
  }

  var loadingClearTimer = null;

  function toggleLoading(loading) {
    // Same body-class contract as assets/collection.js's native
    // eventCollectionShopify.toggleLoading -- theme.css already turns
    // this into the header loading bar, no extra markup needed. Grid
    // rendering itself is now synchronous, but this still gives the
    // visitor the same brief "something happened" feedback the rest of
    // the theme's filtering already provides.
    if (loadingClearTimer) {
      clearTimeout(loadingClearTimer);
      loadingClearTimer = null;
    }
    if (loading) {
      document.body.classList.remove('finish');
      document.body.classList.add('start', 'loading');
    } else {
      document.body.classList.add('finish');
      loadingClearTimer = setTimeout(function () {
        document.body.classList.remove('start', 'loading', 'finish');
        loadingClearTimer = null;
      }, 350);
    }
  }

  function debounce(fn, wait) {
    var t;
    return function () {
      var args = arguments, ctx = this;
      clearTimeout(t);
      t = setTimeout(function () { fn.apply(ctx, args); }, wait);
    };
  }

  // ---------- URL state (shareable/bookmarkable) ----------

  function encodeFilters(activeFilters) {
    var pairs = [];
    for (var key in activeFilters) {
      var set = activeFilters[key];
      if (!set || !set.size) continue;
      set.forEach(function (val) { pairs.push(key + ':' + val); });
    }
    return pairs.join('|');
  }

  function decodeFilters(str) {
    var activeFilters = {};
    if (!str) return activeFilters;
    str.split('|').forEach(function (pair) {
      var idx = pair.indexOf(':');
      if (idx === -1) return;
      var key = pair.slice(0, idx);
      var val = pair.slice(idx + 1);
      if (!activeFilters[key]) activeFilters[key] = new Set();
      activeFilters[key].add(val);
    });
    return activeFilters;
  }

  function getUrlParam(name, search) {
    var match = (search || window.location.search).match(new RegExp('[?&]' + name + '=([^&]*)'));
    return match ? decodeURIComponent(match[1]) : '';
  }

  function readStateFromUrl() {
    var cff = getUrlParam('cff');
    if (cff) state.activeFilters = decodeFilters(cff);
    var cfmin = getUrlParam('cfmin');
    var cfmax = getUrlParam('cfmax');
    state.priceMin = cfmin ? parseFloat(cfmin) : null;
    state.priceMax = cfmax ? parseFloat(cfmax) : null;
    state.sort = getUrlParam('cfsort') || '';
    var cfpage = getUrlParam('cfpage');
    state.page = cfpage ? Math.max(1, parseInt(cfpage, 10)) : 1;
    return !!(cff || cfmin || cfmax || getUrlParam('cfsort') || cfpage);
  }

  function pushUrlState() {
    var params = [];
    var sidebarEl = document.getElementById('CollectionSidebar');
    if (sidebarEl && sidebarEl.getAttribute('data-vendor-mode') === 'true') {
      var vq = getUrlParam('q');
      if (vq) params.push('q=' + encodeURIComponent(vq));
    }
    var cff = encodeFilters(state.activeFilters);
    if (cff) params.push('cff=' + encodeURIComponent(cff));
    if (state.priceMin !== null && !isNaN(state.priceMin)) params.push('cfmin=' + state.priceMin);
    if (state.priceMax !== null && !isNaN(state.priceMax)) params.push('cfmax=' + state.priceMax);
    if (state.sort) params.push('cfsort=' + encodeURIComponent(state.sort));
    if (state.page > 1) params.push('cfpage=' + state.page);
    if (state.view === 'list') params.push('view=list');
    var url = getCollectionUrl() + (params.length ? '?' + params.join('&') : '');
    window.history.replaceState({}, '', url);
  }

  // ---------- matching ----------

  function matchesAll(product) {
    var af = state.activeFilters;
    for (var key in af) {
      var set = af[key];
      if (!set || !set.size) continue;
      var value = key === 'vendor' ? product.vendor : product.mf[key];
      var values = splitFieldValues(value, key);
      if (!values.length) return false;
      if (!values.some(function (v) { return set.has(v); })) return false;
    }
    if (state.priceMin !== null && !isNaN(state.priceMin) && product.price < state.priceMin) return false;
    if (state.priceMax !== null && !isNaN(state.priceMax) && product.price > state.priceMax) return false;
    return true;
  }

  function computeMatches() {
    var products = window.SF_FULL_PRODUCTS || [];
    // Products are already in the correct sort order -- see
    // window.SFC_fetchForSort, called whenever state.sort changes -- so
    // matching just filters, it never needs to re-sort.
    return products.filter(matchesAll);
  }

  // ---------- sidebar + chips ----------

  function updateSidebar(justChangedKey) {
    if (typeof window.SFC_renderSidebar === 'function') {
      window.SFC_renderSidebar(state.activeFilters, state.priceMin, state.priceMax, justChangedKey);
    }
  }

  // The price slider's track/labels already update live every tick via
  // syncPriceVisuals() below, with no sidebar rebuild involved -- so the
  // (expensive) full facet-count recompute + re-render only needs to catch
  // up once dragging/typing pauses, not on every single tick.
  var updateSidebarDebounced = debounce(updateSidebar, 150);

  // Keep in sync with the identical suffix map/logic in
  // collection-custom-facets.js's displayValue() — chips should show the
  // same unit-suffixed text as the checkbox it came from.
  var UNIT_SUFFIXES = { wattage: 'W', cct_kelvin: 'K', voltage: 'V', equivalent_wattage: 'W' };

  function chipLabel(val, key) {
    var lower = String(val).toLowerCase();
    if (lower === 'true') return 'Yes';
    if (lower === 'false') return 'No';
    var suffix = key && UNIT_SUFFIXES[key];
    if (suffix) {
      return String(val).trim().split('/').map(function (part) {
        var p = part.trim();
        return /^\d+(\.\d+)?$/.test(p) ? p + suffix : p;
      }).join('/');
    }
    return val;
  }

  function renderChips() {
    var wrap = document.getElementById('sf-active-filters');
    if (!wrap) return;
    var list = wrap.querySelector('ol');
    if (!list) return;

    var chips = [];
    for (var key in state.activeFilters) {
      var set = state.activeFilters[key];
      if (!set || !set.size) continue;
      set.forEach(function (val) {
        chips.push({ label: chipLabel(val, key), key: key, value: val });
      });
    }
    var hasPrice = (state.priceMin !== null && !isNaN(state.priceMin)) || (state.priceMax !== null && !isNaN(state.priceMax));

    if (!chips.length && !hasPrice) {
      wrap.style.display = 'none';
      list.innerHTML = '';
      return;
    }

    wrap.style.display = '';
    var html = '';
    chips.forEach(function (chip, i) {
      html +=
        '<li class="filter_remove_item"><button type="button" style="font:inherit;border:0;" class="sf-chip-remove link-filter inline-flex align-center gap-20 grey-bg rounded-5 btn-hover lh-normal px-20 py-10 transition-short pointer uppercase-first-letter" data-chip-index="' +
        i + '">' + escapeHtml(chip.label) +
        '<svg width="9" height="9" viewBox="0 0 9 9" fill="none" class="transition"><use href="#icon-close" /></svg></button></li>';
    });
    if (hasPrice) {
      html +=
        '<li class="filter_remove_item"><button type="button" id="sf-chip-price" style="font:inherit;border:0;" class="link-filter inline-flex align-center gap-20 grey-bg rounded-5 btn-hover lh-normal px-20 py-10 transition-short pointer uppercase-first-letter">' +
        money(state.priceMin || 0) + ' - ' + money(state.priceMax || 0) +
        '<svg width="9" height="9" viewBox="0 0 9 9" fill="none" class="transition"><use href="#icon-close" /></svg></button></li>';
    }
    html +=
      '<li><button type="button" id="sf-clear-all-chip" style="font:inherit;" class="filters_clear border-0 link-filter inline-flex align-center gap-20 rounded-5 btn-active lh-normal px-20 py-10 transition btn-primary body_weight">Clear All</button></li>';
    list.innerHTML = html;

    list.querySelectorAll('.sf-chip-remove').forEach(function (btn) {
      btn.addEventListener('click', function () {
        var chip = chips[parseInt(btn.getAttribute('data-chip-index'), 10)];
        if (!chip) return;
        state.activeFilters[chip.key].delete(chip.value);
        state.page = 1;
        onStateChanged(chip.key);
      });
    });
    var priceChip = document.getElementById('sf-chip-price');
    if (priceChip) {
      priceChip.addEventListener('click', function () {
        state.priceMin = null;
        state.priceMax = null;
        state.page = 1;
        onStateChanged('price');
      });
    }
    var clearAll = document.getElementById('sf-clear-all-chip');
    if (clearAll) clearAll.addEventListener('click', clearAllFilters);
  }

  function clearAllFilters() {
    state.activeFilters = {};
    state.priceMin = null;
    state.priceMax = null;
    state.page = 1;
    onStateChanged();
  }

  // ---------- card rendering ----------

  // A simplified but functional card -- not a byte-for-byte clone of
  // snippets/product-item.liquid (700+ lines: wishlist, compare,
  // quickview, hover image swap, ratings, color swatches). Reuses the
  // theme's real classes for the pieces it does cover (image wrapper,
  // sale badge, title, price block, matching snippets/price.liquid) so
  // it sits visually consistent with native cards elsewhere on the site.
  // Injected once: a dedicated class instead of the theme's usual
  // "--aspect-ratio" inline-style convention, because theme.css already
  // has a client customization (see the "PRODUCT IMAGE FIX" comment in
  // assets/product-card.css) that forces object-fit: contain !important
  // on anything matching that pattern -- reusing it would silently block
  // the cover behavior requested here. This class also drives the
  // hover-swap to each product's second image.
  var CARD_STYLE_ID = 'sfc-card-styles';
  function ensureCardStyles() {
    if (document.getElementById(CARD_STYLE_ID)) return;
    var style = document.createElement('style');
    style.id = CARD_STYLE_ID;
    style.textContent =
      '.sfc-media-box{position:relative;overflow:hidden;display:block;aspect-ratio:1/1;}' +
      '.sfc-media-box img{position:absolute;inset:0;width:100%;height:100%;object-fit:cover;display:block;transition:opacity .3s ease;}' +
      '.sfc-media-box img.sfc-img-secondary{opacity:0;}' +
      '.sfc-media-box:hover img.sfc-img-secondary{opacity:1;}' +
      '.sfc-media-box:hover img.sfc-img-primary{opacity:0;}';
    document.head.appendChild(style);
  }

  function renderCard(product) {
    ensureCardStyles();
    var img = product.image;
    var hoverImg = product.hoverImage;
    var onSale = product.compareAtPrice && product.compareAtPrice > product.price;
    var pct = onSale ? Math.round(((product.compareAtPrice - product.price) / product.compareAtPrice) * 100) : 0;
    var url = getCollectionUrl().split('/collections/')[0] + '/products/' + product.handle;

    var imageHtml =
      (img ? '<img class="sfc-img-primary" src="' + escapeHtml(img.url) + '" alt="' + escapeHtml(img.altText || product.title) + '" loading="lazy">' : '') +
      (hoverImg ? '<img class="sfc-img-secondary" src="' + escapeHtml(hoverImg.url) + '" alt="" loading="lazy">' : '');

    var badgeHtml = onSale
      ? '<div class="product__badges-sale product__badges-type-percent product__badges-inner align-self-start px-15 sale inline-flex content-center subheading_weight text-center lh-normal pointer-none absolute top-sm-15 top-10 left-sm-15 left-10 z-1" style="background-color:#e21c21;color:#fff;">-' + pct + '%</div>'
      : '';

    var priceHtml = onSale
      ? '<div class="card-product-price flex align-center flex-wrap gap-5 relative">' +
        '<div class="price-regular primary-color"><span class="price heading_weight primary-color price-same-style price--special">' + money(product.price) + '</span></div>' +
        '<div class="price-regular"><s class="price-item compare-price dark-grey">' + money(product.compareAtPrice) + '</s></div>' +
        '</div>'
      : '<div class="card-product-price flex align-center flex-wrap gap-5 relative">' +
        '<div class="price-regular"><span class="price price-same-style heading-style">' + money(product.price) + '</span></div>' +
        '</div>';

    var addToCartHtml = product.available
      ? '<button type="button" class="btn-primary sfc-add-to-cart w-full mt-10" data-product-id="' + escapeHtml(product.id) + '" data-handle="' + escapeHtml(product.handle) + '">' +
        '<span class="sfc-atc-label">Add To Cart</span>' +
        '</button>'
      : '<button type="button" class="btn-primary w-full mt-10" disabled style="opacity:.5;cursor:not-allowed;">Sold Out</button>';

    // <button-wishlist>/<button-quickview> are already globally defined
    // custom elements (assets/theme.js) that auto-upgrade on insertion --
    // this just reuses their exact native markup/data attributes so they
    // behave identically to real product-item cards, no new JS needed.
    var actionHtml =
      '<div class="product-item__action transition flex flex-column gap-5 absolute bottom-10 right-10">' +
      '<div class="product-item__button-action heading-color transition-btn flex gap-5 flex-column bottom-10 right-10">' +
      '<button-wishlist tabindex="0" class="product-item__button pointer product-item__button-wishlist tooltip relative btn-hover transition-btn btn-white rounded-50 p-0 shadow w-45 h-45 inline-flex content-center" data-product-id="' + escapeHtml(product.id) + '" data-action="go_to_page">' +
      '<svg width="14" height="13" fill="none"><use href="#icon-wishlist" /></svg>' +
      '</button-wishlist>' +
      '<button-quickview data-custom-class="quickview" tabindex="0" class="product-item__button pointer product-item__button-quickview tooltip relative btn-hover transition-btn btn-white rounded-50 p-0 shadow w-45 h-45 inline-flex content-center" data-url="/products/' + escapeHtml(product.handle) + '?section_id=product-quickview&ajax=1">' +
      '<svg width="14" height="12" fill="none" class="hidden-on-load"><use href="#icon-quickview" /></svg>' +
      '</button-quickview>' +
      '</div>' +
      '</div>';

    return (
      '<div class="grid__item motion-item" style="opacity:1;">' +
      '<div class="product-item product__item-js" data-update-url="false">' +
      '<div class="product-item__wrapper block product-grid">' +
      '<div class="product-item__inner hover-effect overflow-hidden relative">' +
      '<a href="' + escapeHtml(url) + '" class="sfc-media-box rounded block">' + imageHtml + '</a>' +
      badgeHtml +
      actionHtml +
      '</div>' +
      '<div class="product-item__information word-break text-left mt-15">' +
      (product.vendor ? '<div class="product-item__vendor dark-grey flex justify-between align-center">' +
        '<span class="product-item__vendor-name">' + escapeHtml(product.vendor) + '</span>' +
        (product.sku ? '<span class="product-item__sku">SKU : ' + escapeHtml(product.sku) + '</span>' : '') +
        '</div>' : '') +
      '<a href="' + escapeHtml(url) + '" class="product-item__name heading-style no-underline fs-custom fw-custom block lh-normal">' + escapeHtml(product.title) + '</a>' +
      '<div class="product-item__price flex gap-custom lh-normal">' + priceHtml + '</div>' +
      addToCartHtml +
      '</div>' +
      '</div>' +
      '</div>' +
      '</div>'
    );
  }

  // ---------- add to cart ----------

  document.addEventListener('click', function (e) {
    var btn = e.target.closest && e.target.closest('.sfc-add-to-cart');
    if (!btn) return;
    e.preventDefault();
    if (btn.disabled) return;
    var productId = btn.getAttribute('data-product-id');
    var product = (window.SF_FULL_PRODUCTS || []).find(function (p) { return p.id === productId; });
    if (!product) return;

    // The full product's real first variant id isn't in the lightweight
    // Storefront dataset fetched for facets/cards -- fetch it directly
    // from the (uncached-by-design) product JSON endpoint just-in-time,
    // rather than bloating every card's data with variant info upfront.
    btn.disabled = true;
    var labelEl = btn.querySelector('.sfc-atc-label');
    var originalLabel = labelEl ? labelEl.textContent : '';
    if (labelEl) labelEl.textContent = 'Adding…';

    fetch('/products/' + product.handle + '.js', { cache: 'no-store' })
      .then(function (r) { return r.json(); })
      .then(function (productJson) {
        var variant = productJson.variants && productJson.variants.find(function (v) { return v.available; });
        if (!variant) throw new Error('No available variant');
        return fetch('/cart/add.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ items: [{ id: variant.id, quantity: 1 }] })
        });
      })
      .then(function (r) {
        if (!r.ok) throw new Error('Add to cart failed');
        return fetch('/cart.js', { cache: 'no-store' });
      })
      .then(function (r) { return r.json(); })
      .then(function (cart) {
        document.querySelectorAll('.cart-count').forEach(function (el) {
          el.textContent = ' (' + cart.item_count + ') ';
        });
        document.dispatchEvent(new CustomEvent('cart:updated', { detail: { cart: cart } }));
        if (labelEl) labelEl.textContent = 'Added ✓';
        setTimeout(function () {
          if (labelEl) labelEl.textContent = originalLabel;
          btn.disabled = false;
        }, 1500);
      })
      .catch(function (err) {
        if (window.console) console.error('Add to cart failed', err);
        if (labelEl) labelEl.textContent = 'Error';
        setTimeout(function () {
          if (labelEl) labelEl.textContent = originalLabel;
          btn.disabled = false;
        }, 1500);
      });
  });

  // ---------- grid rendering ----------

  function scrollToGrid() {
    var grid = document.getElementById('product-grid');
    if (grid && grid.scrollIntoView) grid.scrollIntoView({ behavior: 'smooth', block: 'start' });
  }

  function hideNativePagination() {
    var nativePag = document.getElementById('sf-native-pagination');
    if (nativePag) nativePag.style.display = 'none';
  }

  function renderPagination(totalPages) {
    hideNativePagination();
    var nav = document.getElementById('sf-pagination');
    if (!nav) return;
    if (totalPages <= 1) {
      nav.style.display = 'none';
      var list0 = nav.querySelector('ul');
      if (list0) list0.innerHTML = '';
      return;
    }
    nav.style.display = '';
    var list = nav.querySelector('ul');
    if (!list) return;

    // NOT border:0 -- these buttons rely on the theme's own ".border"
    // utility class (already in itemCls below) for their visible border;
    // an inline border:0 here would win over that class and silently
    // strip it, which is exactly what made these look borderless before.
    var btnReset = 'font:inherit;color:inherit;background:none;padding:0;cursor:pointer;';
    var itemCls = 'pagination__item rounded-50 lh-1 btn-hover no-underline w-full h-44 border inline-flex align-center justify-content-center remove-underline';
    var html = '';
    if (state.page > 1) {
      html += '<li class="w-auto w-44"><button type="button" style="' + btnReset + '" class="sf-page px-15 ' + itemCls + ' pagination__item--next pagination__item-arrow motion-reduce" data-page="' + (state.page - 1) + '" aria-label="Previous"><svg width="5" height="10" fill="none"><use href="#icon-back" /></svg></button></li>';
    }
    var start = Math.max(1, state.page - 3);
    var end = Math.min(totalPages, start + 6);
    if (start > 1) {
      html += '<li class="w-44"><button type="button" style="' + btnReset + '" class="sf-page ' + itemCls + '" data-page="1">1</button></li>';
      if (start > 2) html += '<li class="w-44 inline-flex align-center justify-content-center">...</li>';
    }
    for (var p = start; p <= end; p++) {
      if (p === state.page) {
        html += '<li class="w-44"><a role="link" aria-disabled="true" class="' + itemCls + ' pagination__item--current light" aria-current="page">' + p + '</a></li>';
      } else {
        html += '<li class="w-44"><button type="button" style="' + btnReset + '" class="sf-page ' + itemCls + '" data-page="' + p + '">' + p + '</button></li>';
      }
    }
    if (end < totalPages) {
      if (end < totalPages - 1) html += '<li class="w-44 inline-flex align-center justify-content-center">...</li>';
      html += '<li class="w-44"><button type="button" style="' + btnReset + '" class="sf-page ' + itemCls + '" data-page="' + totalPages + '">' + totalPages + '</button></li>';
    }
    if (state.page < totalPages) {
      html += '<li class="w-44"><button type="button" style="' + btnReset + '" class="sf-page px-15 ' + itemCls + ' pagination__item--prev pagination__item-arrow motion-reduce" data-page="' + (state.page + 1) + '" aria-label="Next"><svg width="5" height="10" fill="none"><use href="#icon-next" /></svg></button></li>';
    }
    list.innerHTML = html;
    list.querySelectorAll('.sf-page').forEach(function (btn) {
      btn.addEventListener('click', function () {
        state.page = parseInt(btn.getAttribute('data-page'), 10);
        renderGridOnly();
        pushUrlState();
        scrollToGrid();
      });
    });
  }

  // Renders #product-grid entirely client-side from window.SF_FULL_PRODUCTS
  // -- no network request, so nothing for a server/CDN cache to go stale
  // on. The sidebar is untouched here; it's re-rendered separately by
  // updateSidebar().
  function renderGridOnly() {
    if (!window.SF_FULL_PRODUCTS_LOADED) return;
    var matches = computeMatches();
    var pageSize = getPageSize();
    var totalPages = Math.max(1, Math.ceil(matches.length / pageSize));
    if (state.page > totalPages) state.page = totalPages;

    var amountEl = document.getElementById('toolbar-amount');
    if (amountEl) amountEl.textContent = matches.length + ' products';

    var grid = document.getElementById('product-grid');
    if (!grid) return;

    hideNativePagination();
    toggleLoading(true);

    if (!matches.length) {
      grid.innerHTML = '<div class="collection collection--empty page-width"><div class="message-info rounded-5 my-0 inline-flex align-center w-full gap-5">No products match the selected filters.</div></div>';
      renderPagination(0);
      toggleLoading(false);
      return;
    }

    var start = (state.page - 1) * pageSize;
    var pageProducts = matches.slice(start, start + pageSize);

    grid.className = (state.view === 'list' ? 'list product-type-list flex flex-column ' : 'grid grid-cols row-gap-custom product-grid grid-columns-' + state.columns + ' ') + 'gap container-products-switch';
    grid.setAttribute('data-view-mode', state.view === 'list' ? state.columns : state.columns);
    grid.innerHTML = pageProducts.map(renderCard).join('');

    renderPagination(totalPages);

    if (window.BlsLazyloadImg && typeof window.BlsLazyloadImg.init === 'function') {
      window.BlsLazyloadImg.init();
    }
    toggleLoading(false);
  }

  // ---------- top-level state-change entry point ----------

  function onStateChanged(justChangedKey) {
    updateSidebar(justChangedKey);
    renderChips();
    renderGridOnly();
    pushUrlState();
  }

  var onStateChangedDebounced = debounce(onStateChanged, 400);

  // ---------- event wiring ----------

  document.addEventListener('change', function (e) {
    if (!e.target.classList.contains('sf-filter-checkbox')) return;
    var key = e.target.getAttribute('data-key');
    var val = e.target.value;
    if (!state.activeFilters[key]) state.activeFilters[key] = new Set();
    if (e.target.checked) state.activeFilters[key].add(val);
    else state.activeFilters[key].delete(val);
    state.page = 1;
    onStateChanged(key);
    scrollToGrid();
  });

  document.addEventListener('click', function (e) {
    if (e.target.id === 'sf-clear-all-drawer') clearAllFilters();
  });

  function syncPriceVisuals(fromSliders) {
    var priceMinEl = document.getElementById('sf-price-min');
    var priceMaxEl = document.getElementById('sf-price-max');
    var sliderMinEl = document.getElementById('sf-range-slider-min');
    var sliderMaxEl = document.getElementById('sf-range-slider-max');
    var track = document.getElementById('sf-price-track');
    var labelMin = document.getElementById('sf-price-label-min');
    var labelMax = document.getElementById('sf-price-label-max');
    if (!priceMinEl || !priceMaxEl || !sliderMinEl || !sliderMaxEl) return;

    var rangeMin = parseFloat(sliderMinEl.getAttribute('min')) || 0;
    var rangeMax = parseFloat(sliderMinEl.getAttribute('max')) || 0;
    var minVal, maxVal;
    if (fromSliders) {
      minVal = parseFloat(sliderMinEl.value);
      maxVal = parseFloat(sliderMaxEl.value);
      if (minVal > maxVal) { var t = minVal; minVal = maxVal; maxVal = t; }
      priceMinEl.value = minVal.toFixed(2);
      priceMaxEl.value = maxVal.toFixed(2);
    } else {
      minVal = parseFloat(priceMinEl.value);
      maxVal = parseFloat(priceMaxEl.value);
      if (isNaN(minVal)) minVal = rangeMin;
      if (isNaN(maxVal)) maxVal = rangeMax;
      sliderMinEl.value = minVal;
      sliderMaxEl.value = maxVal;
    }
    if (track && rangeMax > rangeMin) {
      var from = Math.max(0, Math.min(100, ((minVal - rangeMin) / (rangeMax - rangeMin)) * 100));
      var to = Math.max(0, Math.min(100, ((maxVal - rangeMin) / (rangeMax - rangeMin)) * 100));
      track.style.setProperty('--range-from', from + '%');
      track.style.setProperty('--range-to', to + '%');
    }
    if (labelMin) labelMin.textContent = money(minVal);
    if (labelMax) labelMax.textContent = money(maxVal);
    return { min: minVal, max: maxVal };
  }

  document.addEventListener('input', function (e) {
    if (e.target.id === 'sf-range-slider-min' || e.target.id === 'sf-range-slider-max') {
      var v = syncPriceVisuals(true);
      state.priceMin = v.min;
      state.priceMax = v.max;
      state.page = 1;
      updateSidebarDebounced('price');
      onStateChangedDebounced('price');
    } else if (e.target.id === 'sf-price-min' || e.target.id === 'sf-price-max') {
      var v2 = syncPriceVisuals(false);
      state.priceMin = v2.min;
      state.priceMax = v2.max;
      state.page = 1;
      updateSidebarDebounced('price');
      onStateChangedDebounced('price');
    }
  });

  // Intercept native <filter-sort>, <view-mode>, <grid-mode> clicks
  // before assets/collection.js's own listeners run -- those trigger a
  // native Section Rendering fetch keyed off Shopify's own sort_by/view
  // params against collection.filters, which knows nothing about this
  // system's client-computed matches and would show the wrong results.
  document.addEventListener('click', function (e) {
    var sortEl = e.target.closest && e.target.closest('filter-sort');
    if (sortEl) {
      var value = sortEl.getAttribute('value');
      if (!value) return;
      e.stopPropagation();
      e.stopImmediatePropagation();
      e.preventDefault();
      document.querySelectorAll('filter-sort').forEach(function (fs) { fs.removeAttribute('selected'); });
      sortEl.setAttribute('selected', 'selected');
      var labelEl = document.querySelector('select-sorter .heading-color');
      if (labelEl) labelEl.textContent = sortEl.textContent.trim();
      var selectCustom = sortEl.closest('.select-custom');
      if (selectCustom) selectCustom.classList.remove('active');

      state.sort = value;
      state.page = 1;
      toggleLoading(true);
      window.SFC_fetchForSort(value).then(function () {
        onStateChanged();
      }).catch(function (err) {
        if (window.console) console.error('Sort re-fetch failed', err);
        toggleLoading(false);
      });
      return;
    }

    var viewEl = e.target.closest && e.target.closest('view-mode');
    if (viewEl && window.SF_FULL_PRODUCTS_LOADED) {
      var view = viewEl.getAttribute('data-view');
      if (!view || view === state.view) return;
      e.stopPropagation();
      e.stopImmediatePropagation();
      e.preventDefault();
      state.view = view;
      document.querySelectorAll('view-mode').forEach(function (vm) {
        vm.classList.toggle('active', vm.getAttribute('data-view') === view);
      });
      renderGridOnly();
      pushUrlState();
      return;
    }

    var gridEl = e.target.closest && e.target.closest('grid-mode');
    if (gridEl && window.SF_FULL_PRODUCTS_LOADED) {
      var columns = gridEl.getAttribute('data-grid-mode');
      if (!columns) return;
      var viewChanged = state.view !== 'grid';
      var columnsChanged = columns !== state.columns;
      if (!viewChanged && !columnsChanged) return;
      e.stopPropagation();
      e.stopImmediatePropagation();
      e.preventDefault();
      state.view = 'grid';
      state.columns = columns;
      document.querySelectorAll('grid-mode').forEach(function (gm) {
        gm.classList.toggle('active', gm.getAttribute('data-grid-mode') === columns);
      });
      document.querySelectorAll('view-mode').forEach(function (vm) {
        vm.classList.toggle('active', vm.getAttribute('data-view') === 'grid');
      });
      renderGridOnly();
      pushUrlState();
    }
  }, true);

  // ---------- boot ----------

  document.addEventListener('sfc:facets-ready', function () {
    if (hasHydrated) return;
    hasHydrated = true;
    var hadUrlState = readStateFromUrl();
    updateSidebar();
    if (hadUrlState) {
      if (state.sort) {
        toggleLoading(true);
        window.SFC_fetchForSort(state.sort).then(function () {
          renderChips();
          renderGridOnly();
        });
      } else {
        renderChips();
        renderGridOnly();
      }
    } else {
      // No URL state: the very first paint's server-rendered grid is
      // just collection.products by default sort, which is what a fresh
      // client-side render with no filters produces anyway -- render it
      // now too so it's driven by the exact same code path (correct
      // page size, real availability/badges) as every later interaction.
      renderGridOnly();
    }
  });
})();
